<script lang="ts">
    import type {RecordRow} from 'src/features/record-grid/lib/types';
    import type {PairUnpairEventData} from 'src/features/erp-sutor/pages/project-detail/logbook-pairing/types';
    import type {SearchManager} from 'src/features/search/search-manager/search-manager';
    import {getSutorLogbookPairingContext} from 'src/features/erp-sutor/pages/project-detail/logbook-pairing/sutor-logbook-pairing-context';
    import {getFirstFieldByFieldTypeIdRecursive} from 'src/features/record-grid/lib/grid-fields';
    import {exists} from 'shared/utils/custom-utils';
    import {pipe} from 'core/utils';
    import {loc} from 'shared/utils/pipes';
    import {FOND_PROJECT_BUSINESS_ITEM} from 'src/features/erp-sutor/sutor-fonds';
    import ErpHorizontalTwoSplitLayout from 'src/features/erp/components/erp-split-layouts/ErpHorizontalTwoSplitLayout.svelte';
    import KpGenericTopbar from 'shared/components/kp-generic-topbar/KpGenericTopbar.svelte';
    import LogbookReports from 'src/features/erp-sutor/pages/project-detail/logbook-pairing/LogbookReports.svelte';
    import KpHeading from 'shared/components/kp-heading/KpHeading.svelte';
    import Spacer from 'shared/layouts/flex/Spacer.svelte';
    import KpButton from 'shared/ui-widgets/button/KpButton.svelte';
    import IconedContent from 'shared/ui-widgets/uicons/IconedContent.svelte';
    import GridFieldValue from 'shared/ui-widgets/grid/GridFieldValue.svelte';
    import Flex from 'shared/layouts/flex/Flex.svelte';

    export let openedLogbook: RecordRow;

    const context = getSutorLogbookPairingContext();
    const logbookDateField = getFirstFieldByFieldTypeIdRecursive(openedLogbook, FOND_PROJECT_BUSINESS_ITEM.INSTALLATION_ITEM.dateFieldId);
    const logbookCustomerProjectField = getFirstFieldByFieldTypeIdRecursive(openedLogbook, FOND_PROJECT_BUSINESS_ITEM.INSTALLATION_ITEM.customerProjectFieldId);

    let getSearchManagerPairedReports: () => SearchManager<RecordRow>;
    let getSearchManagerUnpairedReports: () => SearchManager<RecordRow>;

    const handleBackClick = () => {
        context.setOpenedLogbook(null);
    };

    const handlePairUnpair = async (event: CustomEvent<PairUnpairEventData>) => {
        context.addProcessedReport(event.detail.record.id);

        const updatedReport = await context.service.pairReportWithParent(event.detail.record, event.detail.pairingType === 'pair' ? openedLogbook.id : context.projectRecord.id);

        if (!exists(updatedReport)) {
            return;
        }

        const pairedPagination = getSearchManagerPairedReports().getPagination();
        const unpairedPagination = getSearchManagerUnpairedReports().getPagination();

        if (event.detail.pairingType === 'pair') {
            pairedPagination.insertItemAtStart(updatedReport);
            unpairedPagination.removeItems((item) => item.id === updatedReport.id);
        } else {
            pairedPagination.removeItems((item) => item.id === updatedReport.id);
            unpairedPagination.insertItemAtStart(updatedReport);
        }

        context.removeProcessedReport(updatedReport.id);
    };
</script>

<ErpHorizontalTwoSplitLayout pageClass="sutor-reports-processing-page" ratios="{[1, 1]}" resizeDisabled>
    <svelte:fragment slot="topbar">
        <KpGenericTopbar>
            <KpButton on:click={handleBackClick}>
                <IconedContent icon="arrow-left">
                    Zpět/Vybrat jiný montážní deník
                </IconedContent>
            </KpButton>

            <span>Párování výkazů k <span class="logbook-name">{openedLogbook.name}</span></span>
        </KpGenericTopbar>
    </svelte:fragment>

    <svelte:fragment slot="left-panel">
        <Spacer direction="vertical" size="s"/>
        <KpHeading type="h2">Spárované výkazy</KpHeading>
        <Spacer direction="vertical" size="sm"/>
        <small class="text-muted">Tyto výkazy jsou spárovány s vybraným montážním deníkem.</small>
        <Spacer direction="vertical" size="xl"/>

        <LogbookReports logbookRecord="{openedLogbook}" pairingType="unpair" on:pair-unpair={handlePairUnpair} bind:getSearchManager={getSearchManagerPairedReports}/>
    </svelte:fragment>

    <svelte:fragment slot="right-panel">
        <Spacer direction="vertical" size="s"/>
        <KpHeading type="h2">Výkazy k párování</KpHeading>
        <Spacer direction="vertical" size="sm"/>

        <small class="text-muted">Tyto výkazy jsou napárované pouze na zakázku a jejich datum vykázaní a zakázka zákazníka odpovídá datům z montážního deníku.</small>
        <Spacer direction="vertical" size="l"/>

        <Flex direction="column" gap="s">
            <span class="prefiltered-heading">Předfiltrováno pomocí:</span>

            <Flex direction="row" gap="s" alignItems="center">
                <small class="text-muted">Datum:</small>
                <GridFieldValue field="{logbookDateField}"/>
            </Flex>

            <Flex direction="row" gap="s" alignItems="center">
                <small class="text-muted">Zakázka zákazníka:</small>
                <GridFieldValue field="{logbookCustomerProjectField}"/>
            </Flex>

            <Flex direction="row" gap="s" alignItems="center">
                <small class="text-muted">Nadřazená položka:</small>
                <GridFieldValue fondId="{context.projectRecord.fond.id}" value="{pipe(context.projectRecord, loc())}" href="/#!/records/{context.projectRecord.id}"/>
            </Flex>
        </Flex>

        <Spacer direction="vertical" size="xl"/>

        <LogbookReports logbookRecord="{openedLogbook}" pairingType="pair" on:pair-unpair={handlePairUnpair} bind:getSearchManager={getSearchManagerUnpairedReports}/>
    </svelte:fragment>
</ErpHorizontalTwoSplitLayout>

<style lang="less">
    .logbook-name {
        font-weight: bold;
    }

    .prefiltered-heading {
        font-weight: 500;
    }
</style>